import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ComboSelect.css';
import { markUserEdit } from '../hooks/useUnsavedGuard';

/**
 * Shared styled single-select combobox — type-to-search dropdown that matches
 * the legacy ALPACA (DevExpress XAF) lookup control: an input with a trailing
 * chevron that opens a clean, contained, scrollable white list you filter by
 * typing.  Replaces the raw browser `<datalist>` (whose popup we can't style).
 *
 * The popup list is rendered via a PORTAL into `document.body`, positioned
 * with `position: fixed` from the control's own bounding rect — NOT nested
 * inside the control's DOM subtree. This is required so the popup is never
 * clipped by an ancestor's `overflow: auto/hidden` (e.g. AppDialog's
 * scrolling `.ad-body`) — without the portal, a ComboSelect used inside any
 * popup/scroll container renders an unusable, partially-hidden list.
 *
 * `allowCustom` keeps the free-text behaviour of an XAF lookup that also lets
 * you enter a value not in the list (e.g. a brand-new MAC module name).
 */
/** An option may be a bare string (value === label) or a {value,label} pair —
 *  the latter for id-keyed lookups (Position/Department/Division/etc.). */
export type ComboOption = string | { value: string; label: string };

interface ComboSelectProps {
  value: string;
  options: ComboOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  allowCustom?: boolean;
  disabled?: boolean;
  className?: string;
  /** Alternate the list rows white / warm-sand (--ai-row-zebra), like the Sample Registration
   *  pickers. Opt-in, so every other dropdown is unchanged. */
  zebra?: boolean;
  /** Open the dropdown immediately on mount (inline cell editors). */
  autoOpen?: boolean;
  /** Show the trailing clear "✕" that resets the value to empty. Defaults to
   *  `true`. Set `false` for pick-from-list lookups that must always hold a
   *  value (e.g. a required grid cell) — legacy ALPACA XAF lookups have no
   *  clear button, and clearing a required field only yields a broken state.
   *  When true, pressing Backspace/Delete with an empty filter also clears the
   *  value (user request 2026-09-25 — "backspace to remove collector"). */
  clearable?: boolean;
  /** Fires when the popup closes WITHOUT a pick — an outside click (user request
   *  2026-09-25: an open inline-editor combo must release edit mode when the user
   *  clicks elsewhere, so a bulk "Apply to Selected" isn't masked/clobbered by a
   *  still-open editor). A pick / clear / custom commit closes via `onChange`
   *  instead and does NOT call this. Receives the element the outside click
   *  landed on, so an inline-cell editor can re-open the cell that was clicked if
   *  closing the portal swallowed its native click (user report 2026-09-28). */
  onClose?: (target: Node | null) => void;
}

export function ComboSelect({
  value, options, onChange, placeholder = 'Select…', allowCustom = false, disabled = false, className, zebra = false, autoOpen = false, clearable = true, onClose,
}: ComboSelectProps) {
  const [open, setOpen] = useState(autoOpen);
  const [query, setQuery] = useState('');
  /** Keyboard-cursor option index while open (‑1 = no cursor yet). */
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const norm = useMemo(
    () => options.map(o => typeof o === 'string' ? { value: o, label: o } : o),
    [options]);
  const selectedLabel = norm.find(o => o.value === value)?.label ?? (allowCustom ? value : '');

  // Position the portaled popup against the control, and keep it anchored
  // across window resize/scroll while open (the control itself may live
  // inside a scrolling dialog body).
  const reposition = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    // The popup must never be narrower than the control, but a narrow grid
    // cell (e.g. a 90px Status column) forced every option label to the same
    // width and clipped it (Bug: "dropdown values not displayed properly due
    // to an alignment issue"). Let the popup grow to fit its longest option,
    // capped and clamped within the viewport so it never runs off the right
    // edge — same "clamp in-viewport" rule as the grid's column filter popup.
    const longest = Math.max(0, ...norm.map(o => o.label.length));
    const contentWidth = Math.min(420, Math.max(r.width, 60 + longest * 7));
    const left = Math.min(r.left, Math.max(8, window.innerWidth - contentWidth - 8));
    setPos({ top: r.bottom + 2, left, width: contentWidth });
  };
  useLayoutEffect(() => { if (open) reposition(); }, [open]);
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  // Close on outside click — check both the control AND the portaled popup.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (popRef.current?.contains(t)) return;
      setOpen(false);
      // Closed by clicking elsewhere (not a pick) — let an inline-cell editor
      // exit edit mode so it doesn't linger over the resting value, and tell it
      // WHAT was clicked so it can re-open that grid cell if closing this portal
      // swallowed the native click (user report 2026-09-28).
      onClose?.(t);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose]);

  // When opening, focus the filter and seed it with the current value.
  useEffect(() => { if (open) { setQuery(''); setTimeout(() => inputRef.current?.focus(), 0); } }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? norm.filter(o => o.label.toLowerCase().includes(q)) : norm;
  }, [norm, query]);

  // Reset the keyboard cursor on open / filter change; keep it scrolled into view.
  useEffect(() => { setActive(-1); }, [open, query]);
  useEffect(() => {
    if (!open || active < 0) return;
    popRef.current?.querySelector('.cmb-opt.active')?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  // Return focus to the control button when the popup closes, so keyboard nav
  // keeps working and, when this combo sits in a dialog, closing the dialog
  // restores focus to its opener rather than dropping to <body> (user report
  // 2026-09-21).
  const focusTrigger = () => wrapRef.current?.querySelector<HTMLButtonElement>('.cmb-control')?.focus();

  const pick = (v: string) => { markUserEdit(wrapRef.current); onChange(v); setOpen(false); focusTrigger(); };

  const commitCustom = () => {
    const q = query.trim();
    if (allowCustom && q) { markUserEdit(wrapRef.current); onChange(q); setOpen(false); focusTrigger(); }
  };

  return (
    <div ref={wrapRef} className={`cmb${disabled ? ' cmb-disabled' : ''}${className ? ' ' + className : ''}`}>
      <button type="button" className="cmb-control" disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}>
        <span className={value ? 'cmb-val' : 'cmb-ph'}>{selectedLabel || placeholder}</span>
        {value && !disabled && clearable && (
          <i className="ti ti-x cmb-clear" title="Clear"
            onClick={(e) => { e.stopPropagation(); markUserEdit(wrapRef.current); onChange(''); }} />
        )}
        <i className={`ti ti-chevron-down cmb-caret${open ? ' open' : ''}`} />
      </button>
      {open && pos && createPortal(
        <div
          ref={popRef}
          className={`cmb-pop cmb-pop-portal${zebra ? " cmb-pop--zebra" : ""}`}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width }}
        >
          <div className="cmb-search">
            <i className="ti ti-search" />
            <input ref={inputRef} value={query} placeholder="Type to filter…"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                // ↓/↑ move a cursor through the list, Home/End jump, Enter picks the
                // cursor row (else the sole match / a custom value), Esc closes
                // (mouse-free, ADR-0013, user request 2026-09-21).
                if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => (a < 0 ? 0 : Math.min(filtered.length - 1, a + 1))); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => (a < 0 ? filtered.length - 1 : Math.max(0, a - 1))); }
                else if (e.key === 'Home') { e.preventDefault(); setActive(filtered.length ? 0 : -1); }
                else if (e.key === 'End') { e.preventDefault(); setActive(filtered.length - 1); }
                else if (e.key === 'Enter') {
                  e.preventDefault();
                  const cur = filtered[active];
                  if (cur) pick(cur.value);
                  else { const f0 = filtered[0]; if (filtered.length === 1 && f0) pick(f0.value); else commitCustom(); }
                }
                // Backspace / Delete with an empty filter clears the selection —
                // "backspace to remove" (user request 2026-09-25). Only when the
                // combo is clearable, so required pick-from-list lookups are safe.
                else if ((e.key === 'Backspace' || e.key === 'Delete') && query === '' && clearable && value) {
                  e.preventDefault();
                  markUserEdit(wrapRef.current);
                  onChange('');
                  setOpen(false);
                  focusTrigger();
                }
                else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); focusTrigger(); }
              }} />
          </div>
          <div className="cmb-list">
            {filtered.map((o, i) => (
              <div key={o.value} className={`cmb-opt${o.value === value ? ' sel' : ''}${i === active ? ' active' : ''}`} onClick={() => pick(o.value)} title={o.label}>
                {o.label}
                {o.value === value && <i className="ti ti-check" />}
              </div>
            ))}
            {filtered.length === 0 && (
              allowCustom && query.trim()
                ? <div className="cmb-opt cmb-add" onClick={commitCustom}><i className="ti ti-plus" /> Use “{query.trim()}”</div>
                : <div className="cmb-empty">No matches</div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
